import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Search, Filter, Award, Briefcase, Users, FileText, MessageCircle, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SORTS = [
  { id: "leads", label: "Most in demand" },
  { id: "posts", label: "Most prolific" },
  { id: "brands", label: "Most brands" },
  { id: "newest", label: "Just launched" },
];

export default function MogulMarketplace() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [niche, setNiche] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("leads");
  const [loading, setLoading] = useState(false);

  const load = async (n = niche, s = sort) => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/mogul/marketplace?niche=${encodeURIComponent(n)}&sort=${s}`);
      setData(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filter = (n) => {
    setNiche(n);
    setSearch(n);
    load(n, sort);
  };

  const sortChange = (s) => {
    setSort(s);
    load(niche, s);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setNiche(search);
    load(search, sort);
  };

  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]" data-testid="mogul-marketplace-page">
      {/* HEADER */}
      <header className="border-b border-black">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="marketplace-logo">
            <div className="w-6 h-6 bg-black"/>
            <span className="font-display font-black tracking-tighter">VITALITY/<span className="text-[#002FA7]">SS</span></span>
          </Link>
          <button onClick={() => navigate("/pricing")} className="text-eyebrow hover:text-black" data-testid="marketplace-pricing">
            Become a Mogul →
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-black">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-eyebrow mb-4 flex items-center gap-2"><Award size={14}/> Operators Marketplace</div>
          <h1 className="font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9] max-w-4xl">Hire the operator who'll grow your Substack.</h1>
          <p className="mt-6 text-lg text-[#52525B] leading-relaxed max-w-2xl">
            Vetted operators running real Substack engines for real brands. Every one of them is using the same tools you'd use — they just know how to wield them.
          </p>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="border-b border-black bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center gap-4">
          <form onSubmit={onSearchSubmit} className="flex items-stretch border-2 border-black flex-1 min-w-[280px]">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by niche, voice, or specialty (e.g. fitness, B2B SaaS, mindfulness)" data-testid="marketplace-search" className="flex-1 p-2.5 text-sm outline-none"/>
            <button type="submit" className="bg-black text-white px-4 hover:bg-[#002FA7]" data-testid="marketplace-search-button"><Search size={14}/></button>
          </form>
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-eyebrow flex items-center gap-1"><Filter size={11}/> Sort</span>
            {SORTS.map((s) => (
              <button key={s.id} onClick={() => sortChange(s.id)} data-testid={`sort-${s.id}`}
                      className={`text-xs px-2.5 py-1 border ${sort === s.id ? "bg-black text-white border-black" : "border-[#E2E4E9] hover:border-black"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>
        {data?.niches?.length > 0 && (
          <div className="max-w-7xl mx-auto px-6 pb-6 flex flex-wrap gap-2">
            <span className="text-eyebrow mr-2">Quick filters:</span>
            {data.niches.slice(0, 12).map((n) => (
              <button key={n} onClick={() => filter(n)} data-testid={`niche-${n.toLowerCase().replaceAll(" ","-")}`}
                      className={`text-xs px-2 py-1 border ${niche === n ? "bg-[#F59E0B] text-black border-black" : "border-[#E2E4E9] hover:border-black"}`}>
                {n}
              </button>
            ))}
            {niche && (
              <button onClick={() => filter("")} className="text-xs px-2 py-1 text-[#E11D48] hover:underline">clear ×</button>
            )}
          </div>
        )}
      </section>

      {/* RESULTS */}
      <section>
        <div className="max-w-7xl mx-auto px-6 py-10">
          {loading && !data ? (
            <div className="text-sm mono uppercase text-[#A1A1AA] py-20 text-center">loading operators…</div>
          ) : !data || data.count === 0 ? (
            <div className="border-2 border-dashed border-[#E2E4E9] p-16 text-center" data-testid="marketplace-empty">
              <Sparkles size={32} className="mx-auto mb-3 text-[#F59E0B]"/>
              <h3 className="font-display font-bold text-2xl tracking-tight mb-2">No operators match yet.</h3>
              <p className="text-sm text-[#52525B] mb-4">{niche ? `Nothing for "${niche}" — try a different niche.` : "Be the first to publish your Mogul profile."}</p>
              <button onClick={() => navigate("/pricing")} className="bg-black text-white px-5 py-3 text-sm hover:bg-[#F59E0B] hover:text-black inline-flex items-center gap-2" data-testid="marketplace-empty-cta">
                Become a Mogul <ArrowRight size={14}/>
              </button>
            </div>
          ) : (
            <>
              <div className="mb-5 text-sm text-[#52525B]" data-testid="marketplace-count">{data.count} operator{data.count !== 1 ? "s" : ""}{niche ? ` for "${niche}"` : ""}</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-grid">
                {data.results.map((m) => (
                  <Link key={m.slug} to={`/m/${m.slug}`} className="border-2 border-black bg-white p-5 hover:bg-[#FFFBEB] transition-colors group" data-testid={`marketplace-card-${m.slug}`}>
                    <div className="flex items-start gap-3 mb-3">
                      {m.headshot_url ? (
                        <img src={m.headshot_url} alt={m.agency_name} className="w-14 h-14 border-2 border-black object-cover shrink-0"/>
                      ) : (
                        <div className="w-14 h-14 border-2 border-black bg-[#F4F5F6] flex items-center justify-center font-display font-bold text-xl shrink-0">
                          {(m.agency_name || "?")[0]}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-lg tracking-tight truncate" style={{ color: m.accent_color }}>{m.agency_name}</div>
                        {m.tagline && <div className="text-xs text-[#52525B] mt-1 line-clamp-2">{m.tagline}</div>}
                      </div>
                    </div>
                    {m.services?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {m.services.slice(0, 3).map((s, i) => (
                          <span key={i} className="text-[10px] mono px-1.5 py-0.5 bg-[#FAFAFA] border border-[#E2E4E9]">{s}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between border-t border-[#E2E4E9] pt-3 text-xs">
                      <div className="flex gap-3 text-[#52525B]">
                        <span className="flex items-center gap-1"><Users size={11}/> {m.brands_count}</span>
                        <span className="flex items-center gap-1"><FileText size={11}/> {m.posts_count}</span>
                        <span className="flex items-center gap-1"><MessageCircle size={11}/> {m.leads_count}</span>
                      </div>
                      <span className="text-eyebrow group-hover:text-black flex items-center gap-1">View <ArrowRight size={11}/></span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-black">
        <div className="max-w-7xl mx-auto px-6 py-8 text-xs text-[#52525B] flex items-center justify-between flex-wrap gap-3">
          <div>© {new Date().getFullYear()} Vitality/SS</div>
          <a href="/" className="hover:text-black">Back to home →</a>
        </div>
      </footer>
    </div>
  );
}
